import React, { useState, useEffect } from "react";
import { Button } from "@heroui/button";
import { Input, Textarea } from "@heroui/input";

import { Customer } from "../../types/customers";

interface CustomerFormModalProps {
  customer: Customer | null;
  onSave: (customerData: any) => Promise<void>;
  onCancel: () => void;
  loading: boolean;
}

const CustomerFormModal: React.FC<CustomerFormModalProps> = ({
  customer,
  onSave,
  onCancel,
  loading,
}) => {
  const [formData, setFormData] = useState<any>({
    phone_number: "",
    first_name: "",
    last_name: "",
    email: "",
    notes: "",
    address_line1: "",
    address_line2: "",
    city: "",
    state: "",
    zip_code: "",
    delivery_instructions: "",
    addresses_id: null,
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [showAddressFields, setShowAddressFields] = useState(false);

  // Initialize form with existing customer data if editing
  useEffect(() => {
    if (customer) {
      setFormData({
        phone_number: customer.phone_number,
        first_name: customer.first_name,
        last_name: customer.last_name || "",
        email: customer.email || "",
        notes: customer.notes || "",
        address_line1:
          customer.addresses && customer.addresses.length > 0
            ? customer.addresses[0].address_line1
            : "",
        address_line2:
          customer.addresses && customer.addresses.length > 0
            ? customer.addresses[0].address_line2 || ""
            : "",
        city:
          customer.addresses && customer.addresses.length > 0
            ? customer.addresses[0].city
            : "",
        state:
          customer.addresses && customer.addresses.length > 0
            ? customer.addresses[0].state
            : "",
        zip_code:
          customer.addresses && customer.addresses.length > 0
            ? customer.addresses[0].zip_code
            : "",
        delivery_instructions:
          customer.addresses && customer.addresses.length > 0
            ? customer.addresses[0].delivery_instructions || ""
            : "",
        addresses_id:
          customer.addresses && customer.addresses.length > 0
            ? customer.addresses[0].id
            : null,
      });

      // Show address fields if customer has addresses
      setShowAddressFields(customer.addresses && customer.addresses.length > 0);
    }
  }, [customer]);

  // Handle form input changes
  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { name, value } = e.target;

    setFormData((prev:any) => ({
      ...prev,
      [name]: value,
    }));

    // Clear error for this field
    if (errors[name]) {
      setErrors((prev) => {
        const newErrors = { ...prev };

        delete newErrors[name];

        return newErrors;
      });
    }
  };

  // Toggle address section
  const toggleAddressFields = () => {
    setShowAddressFields(!showAddressFields);
  };

  // Form validation
  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    // Basic phone number validation (just ensures it's not empty and has the right format)
    if (!formData.phone_number.trim()) {
      newErrors.phone_number = "Phone number is required";
    } else if (!/^\+?[0-9]{5,20}$/.test(formData.phone_number.trim())) {
      newErrors.phone_number = "Phone number must contain 5-20 digits";
    }

    if (!formData.first_name.trim()) {
      newErrors.first_name = "First name is required";
    }

    // Email validation (if provided)
    if (
      formData.email &&
      !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(formData.email)
    ) {
      newErrors.email = "Valid email is required";
    }

    // Address validation (only if address section is shown)
    if (showAddressFields) {
      if (!formData.address_line1.trim()) {
        newErrors.address_line1 = "Address line 1 is required";
      }

      if (!formData.city.trim()) {
        newErrors.city = "City is required";
      }

      if (!formData.state.trim()) {
        newErrors.state = "State is required";
      }

      if (!formData.zip_code.trim()) {
        newErrors.zip_code = "ZIP code is required";
      }
    }

    setErrors(newErrors);

    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    // Prepare data for submission
    const customerData: any = {
      phone_number: formData.phone_number.trim(),
      first_name: formData.first_name.trim(),
      last_name: formData.last_name.trim() || null,
      email: formData.email.trim() || null,
      notes: formData.notes.trim() || null,
    };

    // Include address if section is shown
    if (showAddressFields) {
      customerData.addresses = [
        {
          address_line1: formData.address_line1.trim(),
          address_line2: formData.address_line2.trim() || null,
          city: formData.city.trim(),
          state: formData.state.trim(),
          zip_code: formData.zip_code.trim(),
          delivery_instructions: formData.delivery_instructions.trim() || null,
          is_default: true,
        },
      ];
      if (formData.addresses_id) {
        customerData.addresses[0].id = formData.addresses_id;
      }
    }

    await onSave(customerData);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-2xl w-full overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <h3 className="font-semibold text-lg text-gray-900 dark:text-white">
            {customer ? "Edit Customer" : "Add Customer"}
          </h3>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="p-6 space-y-6 max-h-[70vh] overflow-y-auto">
            {/* Customer Information Section */}
            <div className="space-y-4">
              <h4 className="text-md font-medium text-gray-900 dark:text-white">
                Customer Information
              </h4>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Input
                    isRequired
                    errorMessage={errors.first_name}
                    isInvalid={!!errors.first_name}
                    label="First Name"
                    name="first_name"
                    placeholder="Enter first name"
                    value={formData.first_name}
                    onChange={handleChange}
                  />
                </div>

                <div>
                  <Input
                    label="Last Name"
                    name="last_name"
                    placeholder="Enter last name"
                    value={formData.last_name}
                    onChange={handleChange}
                  />
                </div>
              </div>

              <div>
                <Input
                  isRequired
                  errorMessage={errors.phone_number}
                  isInvalid={!!errors.phone_number}
                  label="Phone Number"
                  name="phone_number"
                  placeholder="Enter phone number"
                  value={formData.phone_number}
                  onChange={handleChange}
                />
              </div>

              <div>
                <Input
                  errorMessage={errors.email}
                  isInvalid={!!errors.email}
                  label="Email"
                  name="email"
                  placeholder="Enter email address"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                />
              </div>

              <div>
                <Textarea
                  label="Notes"
                  name="notes"
                  placeholder="Enter customer notes"
                  rows={3}
                  value={formData.notes}
                  onChange={handleChange}
                />
              </div>
            </div>

            {/* Address Section Toggle */}
            <div className="pt-2">
              <Button
                className="text-sm"
                variant="light"
                onPress={toggleAddressFields}
              >
                {showAddressFields ? "Hide Address" : "Add Address"}
              </Button>
            </div>

            {/* Address Section (Collapsible) */}
            {showAddressFields && (
              <div className="space-y-4 pt-2 border-t border-gray-200 dark:border-gray-700">
                <h4 className="text-md font-medium text-gray-900 dark:text-white pt-2">
                  Delivery Address
                </h4>

                <div>
                  <Input
                    errorMessage={errors.address_line1}
                    isInvalid={!!errors.address_line1}
                    isRequired={showAddressFields}
                    label="Address Line 1"
                    name="address_line1"
                    placeholder="Enter address line 1"
                    value={formData.address_line1}
                    onChange={handleChange}
                  />
                </div>

                <div>
                  <Input
                    label="Address Line 2"
                    name="address_line2"
                    placeholder="Enter address line 2 (optional)"
                    value={formData.address_line2}
                    onChange={handleChange}
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Input
                      errorMessage={errors.city}
                      isInvalid={!!errors.city}
                      isRequired={showAddressFields}
                      label="City"
                      name="city"
                      placeholder="Enter city"
                      value={formData.city}
                      onChange={handleChange}
                    />
                  </div>

                  <div>
                    <Input
                      errorMessage={errors.state}
                      isInvalid={!!errors.state}
                      isRequired={showAddressFields}
                      label="State"
                      name="state"
                      placeholder="Enter state"
                      value={formData.state}
                      onChange={handleChange}
                    />
                  </div>
                </div>

                <div>
                  <Input
                    errorMessage={errors.zip_code}
                    isInvalid={!!errors.zip_code}
                    isRequired={showAddressFields}
                    label="ZIP Code"
                    name="zip_code"
                    placeholder="Enter ZIP code"
                    value={formData.zip_code}
                    onChange={handleChange}
                  />
                </div>

                <div>
                  <Textarea
                    label="Delivery Instructions"
                    name="delivery_instructions"
                    placeholder="Enter any special delivery instructions"
                    rows={2}
                    value={formData.delivery_instructions}
                    onChange={handleChange}
                  />
                </div>
              </div>
            )}
          </div>

          <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex justify-end space-x-2">
            <Button disabled={loading} variant="light" onPress={onCancel}>
              Cancel
            </Button>
            <Button color="primary" isLoading={loading} type="submit">
              {customer ? "Update Customer" : "Create Customer"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CustomerFormModal;
